import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import {
  deleteFleetVehicleServiceAPI,
  getFleetVehicleServicesAPI,
} from "services/api/fleet/vehicles/services/api";
import { initialFleetVehicleServiceSearchForm } from "services/api/fleet/vehicles/services/consts";
import { buildServiceStatusFields } from "services/api/fleet/vehicles/services/transformations";
import {
  FleetVehicleServiceEditFormValues,
  FleetVehicleServiceRow,
  FleetVehicleServiceSearchFormValues,
} from "services/api/fleet/vehicles/services/types";
import { VehicleLocationFormValues } from "services/api/fleet/vehicles/locations/types";
import {
  FleetVehicleDetailFormValues,
  FleetVehicleStatusDetailFormValues,
  FleetVehicleStatusEnum,
} from "services/api/fleet/vehicles/types";
import { Pagination } from "services/search";

// UI
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";

// Navigacija
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalne komponente
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleStatusFlow from "ui/modules/fleet/vehicles/pages/components/VehicleStatusEditCard/components/VehicleStatusFlow/VehicleStatusFlow";
import VehicleServiceEditCard from "../VehicleServiceEditCard/VehicleServiceEditCard";
import ServiceSearchFilters from "./components/ServiceSearchFilters/ServiceSearchFilters";
import ServiceSearchResults from "./components/ServiceSearchResults/ServiceSearchResults";
import {
  VehicleServiceSearchPageCmp,
  VehicleServiceSearchPageProps,
} from "./types";

/**
 * Servisni nalozi vozila. Nalog se u pravilu otvara promjenom statusa u "Nedostupno –
 * Servis"; ovdje se unosi naknadno (prošli servis) i uređuje postojeći.
 */
const VehicleServiceSearchPage: VehicleServiceSearchPageCmp = (
  _props: VehicleServiceSearchPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [vehicle, setVehicle] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleServiceRow>
  >(initialDataWithPagination);
  const [formValues, setFormValues] = useState<FleetVehicleServiceSearchFormValues>(
    initialFleetVehicleServiceSearchForm
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serviceId, setServiceId] = useState<string>("");
  const [showEditCard, setShowEditCard] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  // Ponuda promjene statusa nakon unosa novog naloga.
  const [showServiceStatusDialog, setShowServiceStatusDialog] = useState<boolean>(false);
  const [showStatusFlow, setShowStatusFlow] = useState<boolean>(false);
  const [statusPrefill, setStatusPrefill] =
    useState<Partial<FleetVehicleStatusDetailFormValues> | null>(null);

  useEffect(() => {
    if (!id) return;

    fetchVehicle(id);
    fetchSearchResult(id, initialDataWithPagination.pagination, formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVehicle = async (vehicleId: string) => {
    try {
      setVehicle(await getFleetVehicleAPI(vehicleId));
    } catch {
      setVehicle(initialFleetVehicleDetailForm);
    }
  };

  const fetchSearchResult = async (
    vehicleId: string,
    pagination: Pagination,
    values: FleetVehicleServiceSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(
        await getFleetVehicleServicesAPI(pagination, { ...values, vehicleId })
      );
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback(
    (values: FleetVehicleServiceSearchFormValues) => {
      setFormValues(values);
      if (id) fetchSearchResult(id, searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, searchResults.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      if (id) fetchSearchResult(id, pagination, formValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, formValues]
  );

  const handleAddNew = useCallback(() => {
    setServiceId("");
    setShowEditCard(true);
  }, []);

  const handleRowClick = useCallback((rowId: string) => {
    setServiceId(rowId);
    setShowEditCard(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditCard(false);
    setServiceId("");
  }, []);

  const handleEditSuccess = useCallback(
    (
      values?: FleetVehicleServiceEditFormValues,
      location?: VehicleLocationFormValues | null
    ) => {
      if (id) fetchSearchResult(id, searchResults.pagination, formValues);

      // Nalog se ovdje unosi i naknadno (prošli servis), pa novi nalog ne znači nužno da
      // je vozilo kod servisera — promjena statusa se nudi, a ne radi sama od sebe.
      const isNew = !serviceId;
      if (
        isNew &&
        values &&
        vehicle.status !== FleetVehicleStatusEnum.UNAVAILABLE_SERVICE
      ) {
        const { location: serviceLocation, ...fields } = buildServiceStatusFields(
          values,
          location ?? null
        );

        // Lokacija ide dalje samo ako je nalog nosi; inače je bolje pustiti toku da je
        // predloži (vrsta "Servis" + odabrani serviser) nego mu je ugasiti.
        setStatusPrefill(
          serviceLocation ? { ...fields, location: serviceLocation } : fields
        );
        setShowServiceStatusDialog(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, serviceId, searchResults.pagination, formValues, vehicle.status]
  );

  const handleServiceStatusConfirm = useCallback(() => {
    setShowServiceStatusDialog(false);
    setShowStatusFlow(true);
  }, []);

  const handleServiceStatusCancel = useCallback(() => {
    setShowServiceStatusDialog(false);
    setStatusPrefill(null);
  }, []);

  const handleStatusFlowClose = useCallback(() => {
    setShowStatusFlow(false);
    setStatusPrefill(null);
  }, []);

  const handleStatusSaved = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, rowId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setServiceId(rowId);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!serviceId || !id) return;

    try {
      await deleteFleetVehicleServiceAPI(serviceId, t);
      await fetchSearchResult(id, searchResults.pagination, formValues);
    } catch (err) {
      console.error("Greška prilikom brisanja servisnog naloga:", err);
    }

    setOpenDialog(false);
    setServiceId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, id, searchResults.pagination, formValues, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setServiceId("");
    setOpenDialog(false);
  }, []);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={vehicle}
            title="Servisi"
            icon="/media/icons/service.svg"
            iconColor="stroke-[#edeef8]"
            onStatusChange={handleStatusSaved}
          >
            {/* Prodano vozilo je arhivirano — novi nalozi se ne otvaraju. */}
            {vehicle.status !== FleetVehicleStatusEnum.SOLD && (
              <Button
                variant="primary"
                children="Novi nalog"
                startIcon="/media/icons/plus.svg"
                onClick={handleAddNew}
              />
            )}
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <ServiceSearchFilters
            formValues={formValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema servisnih naloga za ovo vozilo" />
          ) : (
            <ServiceSearchResults
              searchResults={searchResults}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>

      {showEditCard && (
        <VehicleServiceEditCard
          show={showEditCard}
          vehicleId={id}
          serviceId={serviceId || undefined}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      <Dialog
        open={openDialog}
        title="Brisanje servisnog naloga"
        message="Jeste li sigurni da želite obrisati servisni nalog?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteDialogClose}
      />

      <Dialog
        open={showServiceStatusDialog}
        title="Je li vozilo na servisu?"
        message="Servisni nalog je spremljen. Ako je vozilo kod servisera, status se može odmah promijeniti."
        confirmText="Promijeni status"
        cancelText="Ne, hvala"
        onConfirm={handleServiceStatusConfirm}
        onCancel={handleServiceStatusCancel}
      />

      {/* Nalog je već spremljen, pa tok kreće odmah na poljima statusa — popunjenima iz
          naloga — i ne otvara novi nalog. */}
      <VehicleStatusFlow
        show={showStatusFlow}
        vehicleId={id}
        currentStatus={
          (vehicle.status as FleetVehicleStatusEnum) ?? FleetVehicleStatusEnum.AVAILABLE
        }
        status={showStatusFlow ? FleetVehicleStatusEnum.UNAVAILABLE_SERVICE : null}
        prefill={statusPrefill ?? undefined}
        skipServiceOrder
        onClose={handleStatusFlowClose}
        onSaved={handleStatusSaved}
      />
    </>
  );
};

VehicleServiceSearchPage.route =
  FleetVehicleRoutes.FleetVehicleServiceSearchPageRouter;

export default VehicleServiceSearchPage;
